import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close,
  DeleteOutline,
  Download,
  Image as ImageIcon,
  InsertDriveFile,
  PictureAsPdf,
  UploadFile,
  Visibility,
} from '@mui/icons-material';

const getKind = (attachment) => {
  const raw = String(attachment?.kind || attachment?.type || '').toLowerCase();
  if (raw.includes('image')) return 'image';
  if (raw.includes('pdf')) return 'pdf';
  if (raw.includes('document')) return 'document';

  const url = String(attachment?.url || attachment?.file || '');
  if (/\.(png|jpe?g|gif|webp|bmp|svg)(\?|#|$)/i.test(url)) return 'image';
  if (/\.pdf(\?|#|$)/i.test(url)) return 'pdf';
  if (/\.(docx?|xlsx?|pptx?)(\?|#|$)/i.test(url)) return 'document';
  return 'unknown';
};

const getUrl = (attachment) => attachment?.url || attachment?.file || attachment?.file_url || '';

const getIcon = (kind) => {
  if (kind === 'image') return ImageIcon;
  if (kind === 'pdf') return PictureAsPdf;
  return InsertDriveFile;
};

const formatSize = (size) => {
  const n = Number(size || 0);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

export default function PaymentAttachmentPreview({
  attachment,
  onView,
  onDownload,
  onReplace,
  onDelete,
  permissions = {},
}) {
  const [open, setOpen] = React.useState(false);
  const url = getUrl(attachment);
  const kind = getKind(attachment);
  const Icon = getIcon(kind);
  const name = attachment?.name || 'Archivo';
  const canPreview = Boolean(url) && ['image', 'pdf'].includes(kind);
  const allowReplace = permissions.replace !== false && Boolean(onReplace);
  const allowDelete = permissions.delete !== false && Boolean(onDelete);

  const handleView = () => {
    if (onView) {
      onView(attachment);
      return;
    }
    if (canPreview) {
      setOpen(true);
      return;
    }
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleDownload = () => {
    if (onDownload) {
      onDownload(attachment);
      return;
    }
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.85} sx={{ p: 0.75 }}>
          {kind === 'image' && url ? (
            <Box
              component="img"
              src={url}
              alt={name}
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                objectFit: 'cover',
                border: '1px solid',
                borderColor: 'divider',
                flexShrink: 0,
              }}
            />
          ) : (
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                bgcolor: 'action.hover',
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              <Icon fontSize="small" />
            </Box>
          )}

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
              {name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {[kind === 'unknown' ? 'archivo' : kind.toUpperCase(), formatSize(attachment?.size)].filter(Boolean).join(' - ')}
            </Typography>
          </Box>

          <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
            <Tooltip title="Ver">
              <span>
                <IconButton size="small" disabled={!url} onClick={handleView}>
                  <Visibility fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Descargar">
              <span>
                <IconButton size="small" disabled={!url} onClick={handleDownload}>
                  <Download fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            {allowReplace && (
              <Tooltip title="Reemplazar">
                <IconButton size="small" onClick={() => onReplace(attachment)}>
                  <UploadFile fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {allowDelete && (
              <Tooltip title="Eliminar">
                <IconButton size="small" color="error" onClick={() => onDelete(attachment)}>
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="lg">
        <Box
          sx={{
            px: 1.5,
            py: 1,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
            {name}
          </Typography>
          <Stack direction="row" spacing={0.5}>
            {url && (
              <Button
                component="a"
                href={url}
                target="_blank"
                rel="noreferrer"
                size="small"
                variant="contained"
                sx={{ textTransform: 'none', bgcolor: 'rgba(255,255,255,0.16)', boxShadow: 'none' }}
              >
                Abrir
              </Button>
            )}
            <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: 'inherit' }}>
              <Close fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
        <DialogContent
          dividers
          sx={{
            p: 0,
            height: { xs: 460, md: 680 },
            display: 'grid',
            placeItems: 'center',
            bgcolor: kind === 'image' ? '#111827' : 'background.default',
          }}
        >
          {kind === 'image' && url && (
            <Box
              component="img"
              src={url}
              alt={name}
              sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          )}
          {kind === 'pdf' && url && (
            <Box component="object" data={url} type="application/pdf" sx={{ width: '100%', height: '100%' }}>
              <Box component="iframe" title={name} src={url} sx={{ width: '100%', height: '100%', border: 0 }} />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
